import { ref, uploadBytes, getDownloadURL, deleteObject, getBytes } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';

function getUid(): string | null {
  return useAuthStore.getState().user?.uid ?? null;
}

/**
 * Uploads a compressed recipe image to Firebase Storage.
 * Path: users/{uid}/recipeImages/{recipeId}.jpg
 * Returns the public download URL.
 */
export async function uploadRecipeImage(recipeId: string, blob: Blob): Promise<string> {
  const uid = getUid();
  if (!uid) throw new Error('Authentication required to upload images');

  const imageRef = ref(storage, `users/${uid}/recipeImages/${recipeId}.jpg`);
  await uploadBytes(imageRef, blob, {
    contentType: 'image/jpeg',
    customMetadata: { contentDisposition: 'attachment' },
  });

  return getDownloadURL(imageRef);
}

/**
 * Deletes a recipe image from Firebase Storage.
 * Silently ignores storage/object-not-found errors.
 */
export async function deleteRecipeImage(recipeId: string): Promise<void> {
  const uid = getUid();
  if (!uid) return;

  const imageRef = ref(storage, `users/${uid}/recipeImages/${recipeId}.jpg`);
  try {
    await deleteObject(imageRef);
  } catch (error: unknown) {
    const code = (error as { code?: string }).code;
    if (code !== 'storage/object-not-found') {
      throw error;
    }
  }
}

/**
 * Checks whether a URL points to Firebase Storage.
 * Used to decide whether to clean up Storage on image removal/recipe deletion.
 */
export function isFirebaseStorageUrl(url: string | undefined): boolean {
  if (!url) return false;
  return url.includes('firebasestorage.googleapis.com') || url.includes('storage.googleapis.com');
}

/**
 * Decode a Firebase Storage download URL back to its bucket-relative path.
 * Returns null for non-firebasestorage.googleapis.com hosts and malformed URLs.
 * Only the v0 download endpoint is supported (the one `getDownloadURL` returns).
 */
export function parseFirebaseStoragePath(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.hostname !== 'firebasestorage.googleapis.com') return null;
  const oIndex = parsed.pathname.indexOf('/o/');
  if (oIndex === -1) return null;
  const encoded = parsed.pathname.slice(oIndex + 3);
  if (!encoded) return null;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

/**
 * Clone a recipe image from another user's namespace into the current user's
 * storage so a forked cookbook copy survives the original owner's edits/deletes.
 *
 * - External URLs (Unsplash, etc.) pass through unchanged.
 * - Anonymous users (no uid) skip the copy and keep the original URL.
 * - Sources already in the current user's namespace are returned as-is.
 * - Any failure during fetch/upload falls back to the source URL — best-effort.
 */
export async function copyRecipeImageToCurrentUser(
  sourceUrl: string,
  targetRecipeId: string,
): Promise<string> {
  if (!isFirebaseStorageUrl(sourceUrl)) return sourceUrl;
  const currentUid = getUid();
  if (!currentUid) return sourceUrl;

  const sourcePath = parseFirebaseStoragePath(sourceUrl);
  if (!sourcePath) return sourceUrl;
  if (sourcePath.startsWith(`users/${currentUid}/`)) return sourceUrl;

  try {
    const bytes = await getBytes(ref(storage, sourcePath));
    const targetRef = ref(storage, `users/${currentUid}/recipeImages/${targetRecipeId}.jpg`);
    await uploadBytes(targetRef, bytes, {
      contentType: 'image/jpeg',
      customMetadata: { contentDisposition: 'attachment' },
    });
    return await getDownloadURL(targetRef);
  } catch {
    return sourceUrl;
  }
}
